import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

export interface Breadcrumb {
  readonly label: string;
  readonly to?: string;
}

export interface PageHeaderProps {
  readonly title: string;
  readonly description?: string;
  readonly breadcrumbs?: readonly Breadcrumb[];
  /** Primary and secondary actions, right-aligned. Gate each with <RequirePermission>. */
  readonly actions?: ReactNode;
}

/** Header for list and overview pages — the ones that are *about* a collection, not an entity. */
export function PageHeader({
  title,
  description,
  breadcrumbs,
  actions,
}: PageHeaderProps): ReactNode {
  return (
    <Stack spacing={1}>
      {breadcrumbs !== undefined && breadcrumbs.length > 0 && (
        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.75rem' }}>
          {breadcrumbs.map((crumb) =>
            crumb.to === undefined ? (
              <Typography key={crumb.label} variant="caption" color="text.secondary">
                {crumb.label}
              </Typography>
            ) : (
              <Link key={crumb.label} href={crumb.to} variant="caption" color="text.secondary">
                {crumb.label}
              </Link>
            ),
          )}
        </Breadcrumbs>
      )}
      <Stack
        direction="row"
        spacing={2}
        useFlexGap
        sx={{ alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h1" component="h1">
            {title}
          </Typography>
          {description !== undefined && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              {description}
            </Typography>
          )}
        </Box>
        {actions !== undefined && (
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {actions}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
